// T-341 — Schedulazione cassetti pulizie settimanale/mensile.
//
// I cassetti settimanale e mensile "spuntano" solo nel giorno previsto (config
// per-sede su businesses) e restano richiudibili a tendina. Questo modulo è la
// logica PURA (date locali) consumata dalla UI e dal reminder; niente React/IO.
//
// Config:
//  - weekday: giorno ISO 1=Lun..7=Dom (null = non configurato → legacy)
//  - monthDay: giorno del mese 1..31 (clampato all'ultimo giorno dei mesi corti)

export type WeekdayConfig = number | null | undefined; // 1..7
export type MonthDayConfig = number | null | undefined; // 1..31

/** Giorno ISO della settimana di una Date locale: 1=Lun .. 7=Dom. */
export function isoWeekday(d: Date): number {
  return ((d.getDay() + 6) % 7) + 1;
}

/** Numero di giorni nel mese (year, monthIndex 0-11). */
export function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export interface DueState {
  configured: boolean;
  /** Ultima occorrenza del giorno previsto ≤ oggi (mezzanotte locale). */
  lastDue: Date | null;
  /** Prossima occorrenza del giorno previsto ≥ oggi (oggi se oggi è il giorno). */
  nextDue: Date | null;
}

/** Stato di scadenza per il cassetto SETTIMANALE dato il giorno ISO configurato. */
export function weeklyDueState(today: Date, weekday: WeekdayConfig): DueState {
  if (weekday == null) return { configured: false, lastDue: null, nextDue: null };
  const todayW = isoWeekday(today);
  const base = startOfDay(today);
  // diff giorni indietro fino al weekday (0 se oggi è il giorno).
  const backDelta = (todayW - weekday + 7) % 7;
  const lastDue = new Date(base.getFullYear(), base.getMonth(), base.getDate() - backDelta);
  const fwdDelta = (weekday - todayW + 7) % 7;
  const nextDue = new Date(base.getFullYear(), base.getMonth(), base.getDate() + fwdDelta);
  return { configured: true, lastDue, nextDue };
}

/** Data di scadenza mensile in un dato mese, clampata ai mesi corti. */
function monthlyDateIn(year: number, monthIndex: number, day: number): Date {
  const dim = daysInMonth(year, monthIndex);
  return new Date(year, monthIndex, Math.min(day, dim));
}

/** Stato di scadenza per il cassetto MENSILE dato il giorno del mese configurato. */
export function monthlyDueState(today: Date, monthDay: MonthDayConfig): DueState {
  if (monthDay == null) return { configured: false, lastDue: null, nextDue: null };
  const base = startOfDay(today);
  const thisMonth = monthlyDateIn(base.getFullYear(), base.getMonth(), monthDay);
  let lastDue: Date;
  let nextDue: Date;
  if (base.getTime() >= thisMonth.getTime()) {
    lastDue = thisMonth;
    nextDue = base.getTime() === thisMonth.getTime()
      ? thisMonth
      : monthlyDateIn(base.getFullYear(), base.getMonth() + 1, monthDay);
  } else {
    lastDue = monthlyDateIn(base.getFullYear(), base.getMonth() - 1, monthDay);
    nextDue = thisMonth;
  }
  return { configured: true, lastDue, nextDue };
}

/**
 * Il cassetto è ATTIVO (da mostrare aperto) se NON tutte le sue voci risultano
 * eseguite da `sinceMs` in poi. Cassetto vuoto = non attivo (niente da fare).
 */
export function cassettoActiveSince(
  voci: { id: string }[],
  lastDoneMsById: Map<string, number>,
  sinceMs: number,
): boolean {
  if (voci.length === 0) return false;
  return voci.some((v) => (lastDoneMsById.get(v.id) ?? -Infinity) < sinceMs);
}

/**
 * Istante (Date) in cui schedulare la notifica del prossimo giorno previsto, alle
 * `hour`:00 locali, STRETTAMENTE futuro rispetto a `now`. Null se non configurato.
 * Se il giorno previsto è oggi ma l'ora è già passata, salta all'occorrenza
 * successiva (il "da fare ora" è coperto dal reminder pending + dal cassetto aperto).
 */
export function nextDueNotificationAt(
  now: Date,
  state: DueState,
  advance: (d: Date) => Date,
  hour = 9,
): Date | null {
  if (!state.configured || !state.nextDue) return null;
  let candidate = atHour(state.nextDue, hour);
  if (candidate.getTime() <= now.getTime()) {
    candidate = atHour(advance(state.nextDue), hour);
  }
  return candidate;
}

function atHour(day: Date, hour: number): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0, 0);
}

/** Avanza di una settimana (per nextDueNotificationAt sul cassetto settimanale). */
export const advanceWeek = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7);

/** Avanza al mese successivo mantenendo il giorno (clampato). */
export const advanceMonth = (d: Date): Date =>
  monthlyDateIn(d.getFullYear(), d.getMonth() + 1, d.getDate());
